import { ContextualMenu, DirectionalHint, Icon, IContextualMenuItem } from "@fluentui/react";
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";

// Pezzi comuni delle schede di Setup (mockup-setup-v2, approvato il 24/10/2026): pulsanti con il motivo
// quando non si possono usare, interruttori, pillole, matite, barra della scheda, tabelle a larghezza
// fissa che su telefono diventano schede, finestre con Salva e Annulla sempre visibili.

// larghezza del contenuto: sotto 800 px le tabelle diventano schede
export const CARDS_MAX_WIDTH = 800;

export const useWidth = () => {
  const [width, setWidth] = useState<number>(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
};

export const cx = (...names: (string | false | null | undefined)[]) =>
  names.filter(Boolean).join(" ");

// ---------------- pulsanti ----------------

export interface IBtnProps {
  text?: string;
  icon?: string;
  primary?: boolean;
  small?: boolean;
  ghost?: boolean;
  danger?: boolean;
  // motivo per cui non si può usare: il pulsante resta leggibile e lo dice
  why?: string | null;
  spin?: boolean;
  title?: string;
  className?: string;
  id?: string;
  onClick?: () => void;
  elementRef?: React.Ref<HTMLButtonElement>;
  ariaLabel?: string;
}

export const Btn = (props: IBtnProps) => {
  const disabled = !!props.why;
  return (
    <button
      type="button"
      id={props.id}
      ref={props.elementRef}
      className={cx(
        "b",
        props.primary && "pri",
        props.icon && !props.text && "ico",
        props.small && "small",
        props.ghost && "ghost",
        props.danger && "danger",
        props.className
      )}
      aria-disabled={disabled || undefined}
      aria-label={props.ariaLabel ?? (props.text ? undefined : props.title)}
      title={props.why ?? props.title}
      onClick={() => {
        if (!disabled && props.onClick) props.onClick();
      }}
    >
      {props.spin ? (
        <span className="spinner" />
      ) : props.icon ? (
        <Icon iconName={props.icon} className="ico" />
      ) : null}
      {props.text && <span>{props.text}</span>}
    </button>
  );
};

// pulsante spento con il motivo scritto sotto
export const BtnWhy = (props: IBtnProps) =>
  props.why ? (
    <span className="rb">
      <Btn {...props} />
      <span className="rb-why">{props.why}</span>
    </span>
  ) : (
    <Btn {...props} />
  );

// matita: piccola e grigia, sempre a destra del testo della cella
export const Pen = (props: { label: string; onClick: () => void }) => (
  <Btn icon="Edit" title={props.label} className="pen" onClick={props.onClick} />
);

export const PenRow = (props: { children: ReactNode; label: string; onClick?: () => void }) => (
  <div className="cellrow">
    <div className="grow">{props.children}</div>
    {props.onClick && <Pen label={props.label} onClick={props.onClick} />}
  </div>
);

// ---------------- interruttore, casella, pillole ----------------

export const Switch = (props: {
  on: boolean;
  label?: string;
  why?: string | null;
  mini?: boolean;
  saving?: boolean;
  title?: string;
  onClick?: () => void;
}) => (
  <button
    type="button"
    role="switch"
    className={cx("tg", props.on && "on", props.mini && "mini")}
    aria-checked={props.on}
    aria-disabled={!!props.why || props.saving || undefined}
    aria-label={props.label ? undefined : props.title}
    title={props.why ?? props.title}
    onClick={() => {
      if (!props.why && !props.saving && props.onClick) props.onClick();
    }}
  >
    <span className="tr" />
    {props.saving && <span className="spinner" />}
    {props.label && <span className="tg-l">{props.label}</span>}
  </button>
);

export const CheckBox = (props: { on: boolean }) => (
  <span className={cx("cb", props.on && "on")}>{props.on && <Icon iconName="CheckMark" />}</span>
);

export type PillKind = "ok" | "warn" | "err" | "grey";

export const Pill = (props: { kind: PillKind; text: string; title?: string }) => (
  <span className={"pl " + props.kind} title={props.title}>
    {props.text}
  </span>
);

export const StateText = (props: { on: boolean }) =>
  props.on ? <Pill kind="ok" text="Attivo" /> : <Pill kind="grey" text="Spento" />;

// ---------------- testa della scheda: titolo, una riga, barra ----------------

export const PageHead = (props: { title: string; line: string }) => (
  <div className="ph">
    <h2>{props.title}</h2>
    <p>{props.line}</p>
  </div>
);

export interface IChip {
  key: string;
  text: string;
}

export const Chips = (props: { chips: IChip[]; selected: string; onChange: (key: string) => void }) => (
  <span className="chips">
    {props.chips.map((c) => (
      <button
        type="button"
        key={c.key}
        className={cx("chip", props.selected === c.key && "on")}
        aria-pressed={props.selected === c.key}
        onClick={() => props.onChange(c.key)}
      >
        {c.text}
      </button>
    ))}
  </span>
);

export const SearchBox = (props: { value: string; placeholder: string; onChange: (v: string) => void }) => (
  <div className="search">
    <Icon iconName="Search" className="ico" />
    <input
      value={props.value}
      placeholder={props.placeholder}
      aria-label={props.placeholder}
      onChange={(e) => props.onChange(e.target.value)}
    />
    {props.value && (
      <button type="button" className="search-x" title="Cancella la ricerca" onClick={() => props.onChange("")}>
        <Icon iconName="Cancel" />
      </button>
    )}
  </div>
);

// barra: pulsante e filtri a sinistra, conteggio e ricerca a destra (il conteggio una volta sola)
export const Toolbar = (props: {
  left: ReactNode;
  shown: number;
  total: number;
  noun: string;
  search: string;
  onSearch: (v: string) => void;
  placeholder: string;
}) => (
  <div className="tb">
    {props.left}
    <span className="grow" />
    <span className="count">
      {props.shown === props.total ? props.total + " " + props.noun : props.shown + " su " + props.total}
    </span>
    <SearchBox value={props.search} placeholder={props.placeholder} onChange={props.onSearch} />
  </div>
);

export const Empty = (props: { title: string; text?: string; action?: ReactNode }) => (
  <div className="empty">
    <Icon iconName="Product" className="ico" />
    <div className="t">{props.title}</div>
    {props.text && <div className="d">{props.text}</div>}
    {props.action}
  </div>
);

export const Msg = (props: { kind: "ok" | "warn" | "err" | "info"; children: ReactNode }) => (
  <div className={"msg " + props.kind}>
    <Icon
      iconName={{ ok: "CompletedSolid", warn: "Warning", err: "ErrorBadge", info: "Info" }[props.kind]}
      className="ico"
    />
    <span className="mtext">{props.children}</span>
  </div>
);

// ---------------- tabella ----------------

export interface ISetupColumn<T> {
  name: string;
  // larghezza fissa in px; le colonne "grow" si dividono lo spazio che avanza
  width?: number;
  grow?: boolean;
  hide?: boolean;
  render: (row: T) => ReactNode;
}

// righe da 56 px, colonne a larghezza fissa, intestazione ferma quando le righe scorrono (risposta 8)
export function SetupTable<T>(props: {
  columns: ISetupColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  rowClass?: (row: T) => string;
  empty: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [boxWidth, setBoxWidth] = useState<number>(0);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setBoxWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  let cols = props.columns.filter((c) => !c.hide);
  if (boxWidth > 0 && cols.every((c) => c.width) && cols.some((c) => c.grow)) {
    const sum = cols.reduce((n, c) => n + (c.width ?? 0), 0);
    const growSum = cols.filter((c) => c.grow).reduce((n, c) => n + (c.width ?? 0), 0);
    const extra = boxWidth - sum;
    if (extra > 0) {
      cols = cols.map((c) =>
        c.grow ? { ...c, width: (c.width ?? 0) + Math.floor((extra * (c.width ?? 0)) / growSum) } : c
      );
    }
  }
  const scroll = props.rows.length > 12;
  return (
    <div ref={box} className={cx("tbl", scroll && "scroll")}>
      {props.rows.length === 0 ? (
        props.empty
      ) : (
        <table className="t">
          <colgroup>
            {cols.map((c, i) => (
              <col key={i} style={c.width ? { width: c.width + "px" } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {cols.map((c, i) => (
                <th key={i}>{c.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {props.rows.map((r) => (
              <tr key={props.rowKey(r)} className={props.rowClass ? props.rowClass(r) : undefined}>
                {cols.map((c, i) => (
                  <td key={i}>{c.render(r)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// schede compatte (telefono e finestre strette)
export function SetupCards<T>(props: {
  rows: T[];
  rowKey: (row: T) => string | number;
  card: (row: T) => ReactNode;
  empty: ReactNode;
}) {
  return (
    <div className="cards">
      {props.rows.length === 0 ? props.empty : props.rows.map((r) => <div key={props.rowKey(r)}>{props.card(r)}</div>)}
    </div>
  );
}

// ---------------- menu «...» ----------------

export const MoreMenu = (props: { items: IContextualMenuItem[]; title: string }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  return (
    <>
      <Btn icon="More" title={props.title} elementRef={ref} onClick={() => setOpen(!open)} />
      {open && (
        <ContextualMenu
          target={ref}
          items={props.items}
          directionalHint={DirectionalHint.bottomRightEdge}
          onDismiss={() => setOpen(false)}
          onItemClick={() => setOpen(false)}
        />
      )}
    </>
  );
};

// ---------------- campi ----------------

export const Field = (props: {
  label: string;
  required?: boolean;
  hint?: string | null;
  err?: string | null;
  children: ReactNode;
}) => (
  <div className="fld">
    <label>
      {props.label}
      {props.required ? " *" : ""}
    </label>
    {props.children}
    {props.err ? <span className="err">{props.err}</span> : props.hint ? <span className="hint">{props.hint}</span> : null}
  </div>
);

export const TextInput = (props: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  bad?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  ariaLabel?: string;
}) => (
  <input
    className={cx("tf", props.bad && "bad")}
    value={props.value}
    type={props.type ?? "text"}
    placeholder={props.placeholder}
    readOnly={props.readOnly}
    autoFocus={props.autoFocus}
    aria-label={props.ariaLabel}
    autoComplete="off"
    onChange={(e) => props.onChange(e.target.value)}
  />
);

// ---------------- finestra ----------------

// Salva e Annulla sempre visibili in fondo; a sinistra il motivo per cui Salva non si può usare.
// Il clic fuori non chiude (come SmartModal); Esc chiude.
export const Dialog = (props: {
  title: ReactNode;
  ariaTitle: string;
  children: ReactNode;
  footer: ReactNode;
  left?: ReactNode;
  why?: string | null;
  wide?: boolean;
  narrow?: boolean;
  onClose: () => void;
}) => {
  const { onClose } = props;
  const win = useRef<HTMLDivElement>(null);
  // Esc chiude (non mentre una tendina è aperta); Tab resta dentro la finestra
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (e.defaultPrevented || (e.target as HTMLElement)?.tagName === "SELECT") return;
        onClose();
        return;
      }
      if (e.key !== "Tab" || !win.current) return;
      const items = Array.from(
        win.current.querySelectorAll<HTMLElement>("button:not([aria-disabled=true]), input, select, textarea")
      ).filter((x) => x.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  // all'apertura il focus va al primo campo (o ad Annulla); alla chiusura torna da dove era partito
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const el = win.current;
    if (el && !el.contains(document.activeElement)) {
      const field = el.querySelector<HTMLElement>(".mbody input:not([readonly]), .mbody select, .mbody textarea");
      const btn = el.querySelector<HTMLElement>(".mfoot button");
      (field ?? btn)?.focus();
    }
    return () => {
      if (before && document.body.contains(before)) before.focus();
    };
  }, []);
  return (
    <div className="setup2 mback">
      <div ref={win} className={cx("mwin", props.wide && "wide")} role="dialog" aria-modal="true" aria-label={props.ariaTitle}>
        <div className="mhead">
          <div className="t">{props.title}</div>
          <Btn icon="Cancel" title="Chiudi (Esc)" onClick={props.onClose} />
        </div>
        <div className="mbody">{props.children}</div>
        <div className="mfoot">
          {props.left}
          {props.why && <span className="why">{props.why}</span>}
          <div className="right">{props.footer}</div>
        </div>
      </div>
    </div>
  );
};

export const ConfirmDialog = (props: {
  title: string;
  text: ReactNode;
  ok: string;
  danger?: boolean;
  onOk: () => void;
  onClose: () => void;
}) => (
  <Dialog
    title={props.title}
    ariaTitle={props.title}
    onClose={props.onClose}
    footer={
      <>
        <Btn text="Annulla" onClick={props.onClose} />
        <Btn text={props.ok} primary onClick={props.onOk} />
      </>
    }
  >
    <div style={{ lineHeight: "20px" }}>{props.text}</div>
  </Dialog>
);
